// src/platform/ui/offlineBanner.js

import { ref, readonly } from 'vue'

import eventBus from '@/platform/core/eventBus'
import logger from '@/platform/core/logger'

import offlineManager from '@/platform/pwa/offlineManager'

class OfflineBanner {
  constructor() {
    this.visible = ref(false)
    this.isOnline = ref(true)
    this.message = ref('')
    this.status = ref('online')

    this.initialized = false
    this.unsubscribeConnectivity = null
    this.onlineHideTimer = null
  }

  init() {
    if (this.initialized) {
      return
    }

    const currentStatus =
      offlineManager.getStatus()

    this.isOnline.value =
      currentStatus.isOnline

    this.status.value =
      currentStatus.isOnline
        ? 'online'
        : 'offline'

    this.unsubscribeConnectivity =
      eventBus.on(
        'platform.offline.changed',
        event => {
          this.handleConnectivityEvent(
            event
          )
        }
      )

    if (!currentStatus.isOnline) {
      this.showOffline()
    }

    this.initialized = true

    logger.info(
      'Offline banner initialized.',
      {
        isOnline:
          this.isOnline.value
      }
    )
  }

  destroy() {
    if (!this.initialized) {
      return
    }

    if (
      this.unsubscribeConnectivity
    ) {
      this.unsubscribeConnectivity()
      this.unsubscribeConnectivity = null
    }

    if (
      this.onlineHideTimer &&
      typeof window !== 'undefined'
    ) {
      window.clearTimeout(
        this.onlineHideTimer
      )

      this.onlineHideTimer = null
    }

    this.initialized = false

    logger.info(
      'Offline banner destroyed.'
    )
  }

  handleConnectivityEvent(event) {
    if (!event) {
      return
    }

    this.isOnline.value =
      Boolean(
        event.isOnline
      )

    if (this.isOnline.value) {
      this.showOnline()
    } else {
      this.showOffline()
    }
  }

  showOffline() {
    if (
      this.onlineHideTimer &&
      typeof window !== 'undefined'
    ) {
      window.clearTimeout(
        this.onlineHideTimer
      )

      this.onlineHideTimer = null
    }

    this.status.value =
      'offline'

    this.message.value =
      'You are offline. Some NexgbitsIS features may be unavailable.'

    this.visible.value =
      true
  }

  showOnline() {
    this.status.value =
      'online'

    this.message.value =
      'Internet connection restored.'

    this.visible.value =
      true

    if (
      this.onlineHideTimer &&
      typeof window !== 'undefined'
    ) {
      window.clearTimeout(
        this.onlineHideTimer
      )
    }

    if (
      typeof window === 'undefined'
    ) {
      return
    }

    this.onlineHideTimer =
      window.setTimeout(
        () => {
          if (
            this.isOnline.value
          ) {
            this.visible.value =
              false
          }

          this.onlineHideTimer =
            null
        },
        4000
      )
  }

  hide() {
    if (
      !this.isOnline.value
    ) {
      return false
    }

    this.visible.value =
      false

    return true
  }

  getState() {
    return {
      visible:
        readonly(
          this.visible
        ),

      isOnline:
        readonly(
          this.isOnline
        ),

      message:
        readonly(
          this.message
        ),

      status:
        readonly(
          this.status
        )
    }
  }
}

const offlineBanner =
  new OfflineBanner()

export default offlineBanner